import cn from "classnames";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import Cropper from "react-cropper";
import axios from "axios";
import useWindowDimensions from "@helpers/hooks/windowDemensions";
import {
  businessGiftImagesState,
  businessGiftFrameState,
} from "../../../Recoil/Atoms/businessGifts";
import HeartLoader from "../../HeartLoader";
import "../EditImage/style.css";

const ZOOM_STEP = 0.005;
const BASE_URL = process.env.REACT_APP_BASE_URL;

const EditImageBusinessGifts = ({ handleCloseModal: closeModal, modalData }) => {
  // Wrapper to handle both direct close and named close
  const handleCloseModal = (name) => {
    closeModal();
  };
  const cropperRef = useRef(null);
  const { height, width } = useWindowDimensions();
  const imageElement = cropperRef?.current;
  const cropper = imageElement?.cropper;

  // BusinessGifts state
  const [images, setImages] = useRecoilState(businessGiftImagesState);
  const selectedFrame = useRecoilValue(businessGiftFrameState);

  // Local state
  const [zoomvalue, setzoomvalue] = useState(0.0);
  const [zoomMinValue, setZoomMinValue] = useState(0.0);
  const [isLoading, setLoading] = useState(true);
  const [isInitialLoaded, setIsInitialLoaded] = useState(true);
  const [isBwFilter, setIsBwFilter] = useState(false);
  const [isBluewhiteFilter, setIsBluewhiteFilter] = useState(false);
  const isInitial = useRef(true);
  const isMounted = useRef(true);

  // Image data from modalData
  const imageData = modalData?.image || {};
  const businessCode = modalData?.businessCode || "";

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initialize filters from image data
  useEffect(() => {
    if (imageData) {
      setIsBwFilter(imageData.bwFilter || false);
      setIsBluewhiteFilter(imageData.bluewhiteFilter || false);
    }
  }, [imageData]);

  const getCropperSize = () => 252;

  const calCropData = (imgData) => {
    console.log('[EditImageBusinessGifts] calCropData input:', imgData.cropData);

    if (!imgData.cropData) {
      console.log('[EditImageBusinessGifts] No cropData, returning defaults');
      return { top: 0, left: 0 };
    }

    const cropData = imgData.cropData;

    // If cropData has naturalWidth, it came from a previous edit
    if (cropData.naturalWidth) {
      console.log('[EditImageBusinessGifts] Using previous edit cropData');
      return { ...cropData };
    }

    // Convert server coordinates to cropper moveTo coordinates
    let left = cropData.left || 0;
    let top = cropData.top || 0;
    const cropSize = cropData.width;
    const zoomFactor = cropData.zoomFactor || 1.0;
    const naturalHeight = parseInt(imgData.imageHeight);
    const naturalWidth = parseInt(imgData.imageWidth);
    const cropperSize = getCropperSize();

    console.log('[EditImageBusinessGifts] calCropData values:', {
      left, top, cropSize, zoomFactor,
      naturalWidth, naturalHeight, cropperSize,
      storedViewport: cropData.viewport
    });

    let rate;
    if (naturalHeight <= naturalWidth) {
      rate = cropperSize / naturalHeight;
    } else {
      rate = cropperSize / naturalWidth;
    }

    // When there's a zoomFactor > 1.0, calculate viewport position
    if (zoomFactor > 1.0 && cropSize) {
      const zoomedRate = rate * zoomFactor;

      // Use the stored viewport if available (calculated during face detection)
      const storedViewport = cropData.viewport;

      let viewportX, viewportY, viewportSize;
      if (storedViewport && storedViewport.x !== undefined) {
        // Viewport was calculated for original image dimensions (cropSize)
        // Current image may be resized (naturalWidth), so we need to scale
        const scaleFactor = naturalWidth / cropSize;

        // Scale viewport coordinates to match current image dimensions
        viewportX = storedViewport.x * scaleFactor;
        viewportY = storedViewport.y * scaleFactor;
        viewportSize = storedViewport.width * scaleFactor;

        console.log('[EditImageBusinessGifts] Using stored viewport:', {
          original: storedViewport,
          scaleFactor,
          scaled: { x: viewportX, y: viewportY, size: viewportSize }
        });
      } else {
        // Fallback: calculate viewport (may not match original auto-crop exactly)
        viewportSize = naturalWidth / zoomFactor;
        const scaledCropSize = naturalWidth;
        const scaledLeft = left * (naturalWidth / cropSize);
        const scaledTop = top * (naturalWidth / cropSize);
        const centerX = scaledLeft + scaledCropSize / 2;
        const centerY = scaledTop + scaledCropSize / 2;
        viewportX = centerX - viewportSize / 2;
        viewportY = centerY - viewportSize / 2;

        console.log('[EditImageBusinessGifts] Calculated viewport (no stored viewport):', {
          viewportSize, centerX, centerY, viewportX, viewportY
        });
      }

      // Convert viewport position to cropper moveTo coordinates
      left = viewportX * -zoomedRate;
      top = viewportY * -zoomedRate;

      console.log('[EditImageBusinessGifts] Zoomed calculation result:', {
        zoomedRate, resultLeft: left, resultTop: top
      });
    } else {
      left = left * -rate;
      top = top * -rate;
      console.log('[EditImageBusinessGifts] Normal calculation:', { rate, resultLeft: left, resultTop: top });
    }

    return { top, left, zoomFactor };
  };

  const InitialSets = (target) => {
    // Debug: log the FULL imageData structure
    console.log('[EditImageBusinessGifts] ========== InitialSets START ==========');
    console.log('[EditImageBusinessGifts] InitialSets - FULL imageData:', imageData);
    console.log('[EditImageBusinessGifts] InitialSets - key fields:', {
      imageId: imageData.imageId,
      imageUrl: imageData.imageUrl,
      cropImageUrl: imageData.cropImageUrl,
      imageWidth: imageData.imageWidth,
      imageHeight: imageData.imageHeight,
      cropData: imageData.cropData,
      zoomFactor: imageData.cropData?.zoomFactor,
      viewport: imageData.cropData?.viewport,
      borderTypes: imageData.cropData?.borderTypes || imageData.cropData?.contentBounds?.borderTypes,
    });

    // Get saved user zoom - if user manually zoomed before, restore it
    const savedZoom = imageData.cropData?.zoomRatio || 0;
    const autoZoomFactor = imageData.cropData?.zoomFactor || 1.0;
    // Check if image was actually edited before (has naturalWidth from previous edit session)
    const wasManuallyEdited = imageData.cropData?.naturalWidth !== undefined;

    console.log('[EditImageBusinessGifts] Zoom decision:', {
      savedZoom,
      autoZoomFactor,
      wasManuallyEdited,
      hasNaturalWidth: imageData.cropData?.naturalWidth,
      willUseSavedZoom: savedZoom > 0.001 && wasManuallyEdited,
      willUseAutoZoom: autoZoomFactor > 1.0 && !wasManuallyEdited,
    });

    if (savedZoom > 0.001 && wasManuallyEdited) {
      // User has manually zoomed before - use their saved value
      console.log('[EditImageBusinessGifts] Using SAVED zoom (user manually edited before):', savedZoom);
      target.zoomTo(savedZoom);
      if (isMounted.current) {
        setzoomvalue(savedZoom);
      }

      // Apply position after zoom
      const cropData = calCropData(imageData);
      console.log('[EditImageBusinessGifts] calCropData result:', cropData);
      target.moveTo(cropData.left, cropData.top);

    } else if (autoZoomFactor > 1.0) {
      // Apply auto-zoom directly using current zoom ratio as minZoom
      // (zoom event doesn't fire on initial render, so we can't wait for handleOnZoom)
      const canvasData = target.getCanvasData();
      const imgData = target.getImageData();
      const minZoom = canvasData.width / imgData.naturalWidth;
      const autoZoom = minZoom * autoZoomFactor;

      console.log(`[EditImageBusinessGifts] APPLYING AUTO-ZOOM directly:`, {
        minZoom,
        autoZoomFactor,
        autoZoom,
        calculation: `${minZoom.toFixed(4)} * ${autoZoomFactor.toFixed(4)} = ${autoZoom.toFixed(4)}`
      });

      target.zoomTo(autoZoom);
      if (isMounted.current) {
        setzoomvalue(autoZoom);
        setZoomMinValue(minZoom);
      }
      window.sessionStorage.setItem("minZoomLevel", minZoom);

      // Apply position AFTER zoom - critical for correct border avoidance
      const cropData = calCropData(imageData);
      console.log('[EditImageBusinessGifts] Moving cropper to (after auto-zoom):', { left: cropData.left, top: cropData.top });
      target.moveTo(cropData.left, cropData.top);

    } else {
      console.log('[EditImageBusinessGifts] No zoom adjustment needed - using default cropper fit');
      // Apply position for non-zoomed case
      const cropData = calCropData(imageData);
      console.log('[EditImageBusinessGifts] calCropData result:', cropData);
      target.moveTo(cropData.left, cropData.top);
    }

    // Log final cropper state
    const finalCanvasData = target.getCanvasData();
    const finalImageData = target.getImageData();
    console.log('[EditImageBusinessGifts] Final cropper state:', {
      canvasData: { left: finalCanvasData.left, top: finalCanvasData.top, width: finalCanvasData.width, height: finalCanvasData.height },
      imageData: { naturalWidth: finalImageData.naturalWidth, naturalHeight: finalImageData.naturalHeight },
    });
    console.log('[EditImageBusinessGifts] ========== InitialSets END ==========');

    if (isMounted.current) {
      setLoading(false);
      setIsInitialLoaded(false);
    }
  };

  const getZoomLevel = useCallback((imageHeight, imageWidth) => {
    const maxImageDimension = Math.max(imageHeight, imageWidth);
    const zoomMin = +window.sessionStorage.getItem("minZoomLevel") || 0;
    if (maxImageDimension >= 1600) {
      return 2 * zoomMin;
    } else if (maxImageDimension >= 1400) {
      return 1.6 * zoomMin;
    } else if (maxImageDimension >= 1200) {
      return 1.4 * zoomMin;
    } else if (maxImageDimension >= 800) {
      return 1.2 * zoomMin;
    } else {
      return 0.0;
    }
  }, []);

  const handleOnZoom = useCallback((e) => {
    console.log('[EditImageBusinessGifts] handleOnZoom called:', {
      isInitial: isInitial.current,
      oldRatio: e.detail.oldRatio,
      newRatio: e.detail.ratio,
    });

    if (isInitial.current) {
      const minZoom = +e.detail.oldRatio;
      console.log('[EditImageBusinessGifts] ========== INITIAL ZOOM EVENT ==========');
      console.log('[EditImageBusinessGifts] Captured minZoom:', minZoom);

      if (isMounted.current) {
        setZoomMinValue(minZoom);
      }
      window.sessionStorage.setItem("minZoomLevel", minZoom);
      isInitial.current = false;

      // Apply auto-zoom factor from border detection after minZoom is set
      // Read directly from imageData to avoid race condition with state
      const autoZoomFactor = imageData.cropData?.zoomFactor || 1.0;
      const savedZoom = imageData.cropData?.zoomRatio || 0;
      const wasManuallyEdited = imageData.cropData?.naturalWidth !== undefined;

      console.log('[EditImageBusinessGifts] Zoom decision in handleOnZoom:', {
        autoZoomFactor,
        savedZoom,
        wasManuallyEdited,
        hasNaturalWidth: imageData.cropData?.naturalWidth,
        minZoom,
        willApplyAutoZoom: autoZoomFactor > 1.0 && !wasManuallyEdited,
      });

      // Only apply auto-zoom if user hasn't manually edited before (naturalWidth indicates previous edit)
      if (autoZoomFactor > 1.0 && !wasManuallyEdited) {
        const targetZoom = minZoom * autoZoomFactor;
        console.log(`[EditImageBusinessGifts] APPLYING AUTO-ZOOM: ${minZoom.toFixed(4)} * ${autoZoomFactor.toFixed(4)} = ${targetZoom.toFixed(4)}`);
        console.log(`[EditImageBusinessGifts] Auto-zoom details:`, {
          minZoom,
          autoZoomFactor,
          targetZoom,
          savedZoom,
          viewport: imageData.cropData?.viewport,
          contentBounds: imageData.cropData?.contentBounds,
          borderTypes: imageData.cropData?.borderTypes,
        });
        setTimeout(() => {
          if (cropperRef.current?.cropper) {
            const cropper = cropperRef.current.cropper;

            // Log state BEFORE zoom
            const beforeCanvasData = cropper.getCanvasData();
            console.log('[EditImageBusinessGifts] State BEFORE auto-zoom:', {
              canvasData: { left: beforeCanvasData.left, top: beforeCanvasData.top, width: beforeCanvasData.width, height: beforeCanvasData.height },
            });

            cropper.zoomTo(targetZoom);
            if (isMounted.current) {
              setzoomvalue(targetZoom);
            }

            // Re-apply position AFTER zoom - this is critical for correct border avoidance
            const cropData = calCropData(imageData);
            console.log('[EditImageBusinessGifts] Calculated position for after-zoom moveTo:', cropData);
            cropper.moveTo(cropData.left, cropData.top);
            console.log(`[EditImageBusinessGifts] Position applied after zoom: left=${cropData.left.toFixed(2)}, top=${cropData.top.toFixed(2)}`);

            // Log final cropper state
            const canvasData = cropper.getCanvasData();
            const imageDataCropper = cropper.getImageData();
            const cropDataFinal = cropper.getData();
            console.log(`[EditImageBusinessGifts] FINAL cropper state after auto-zoom:`, {
              canvasData: { left: canvasData.left, top: canvasData.top, width: canvasData.width, height: canvasData.height },
              imageData: { naturalWidth: imageDataCropper.naturalWidth, naturalHeight: imageDataCropper.naturalHeight },
              cropData: { x: cropDataFinal.x, y: cropDataFinal.y, width: cropDataFinal.width, height: cropDataFinal.height },
            });
            console.log('[EditImageBusinessGifts] ========== AUTO-ZOOM COMPLETE ==========');
          }
        }, 50);
      } else {
        console.log(`[EditImageBusinessGifts] No auto-zoom needed:`, {
          savedZoom,
          autoZoomFactor,
          wasManuallyEdited,
          reason: wasManuallyEdited ? 'user manually edited before' : (autoZoomFactor <= 1.0 ? 'zoomFactor <= 1.0' : 'unknown'),
        });
        console.log('[EditImageBusinessGifts] ========== INITIAL ZOOM EVENT END (no auto-zoom) ==========');
      }
    } else {
      if (imageData.imageWidth < 800 && imageData.imageHeight < 800) {
        e.preventDefault();
      }
      if (e.detail.ratio >= getZoomLevel(+imageData.imageHeight, +imageData.imageWidth)) {
        e.preventDefault();
      } else {
        if (isMounted.current) {
          setzoomvalue(e.detail.ratio);
        }
      }
    }
  }, [getZoomLevel, imageData]);

  const handleZoomTo = (e) => {
    if (isMounted.current) {
      setzoomvalue(+e.target.value);
    }
    cropper?.zoomTo(+e.target.value);
  };

  const zoomvalueplus = () => {
    if (imageData.imageWidth < 800 && imageData.imageHeight < 800) return;
    if (zoomvalue >= getZoomLevel(+imageData.imageHeight, +imageData.imageWidth)) {
      return;
    } else {
      if (isMounted.current) {
        setzoomvalue((zm) => zm + ZOOM_STEP);
      }
      cropper.zoomTo(zoomvalue + ZOOM_STEP);
    }
  };

  const zoomvalueminus = () => {
    if (zoomvalue < zoomMinValue + ZOOM_STEP) {
      if (isMounted.current) {
        setzoomvalue(zoomMinValue);
      }
      cropper.zoomTo(zoomMinValue);
    } else {
      if (isMounted.current) {
        setzoomvalue((zm) => zm - ZOOM_STEP);
      }
      cropper.zoomTo(zoomvalue - ZOOM_STEP);
    }
  };

  const handleFilterClick = () => {
    if (isMounted.current) {
      setIsBwFilter(!isBwFilter);
      setIsBluewhiteFilter(false);
    }
  };

  const handleBluewhiteFilterClick = () => {
    if (isMounted.current) {
      setIsBluewhiteFilter(!isBluewhiteFilter);
      setIsBwFilter(false);
    }
  };

  const getCropData = async () => {
    setLoading(true);

    if (typeof cropper !== "undefined") {
      const cropbox_data = cropper.getCanvasData();
      const image_data = cropper.getImageData();
      const crop_data = cropper.getData();

      // Calculate crop coordinates for server
      const serverCropData = {
        left: Math.round(crop_data.x),
        top: Math.round(crop_data.y),
        width: Math.round(crop_data.width),
        height: Math.round(crop_data.height),
      };

      try {
        // Call the business gifts update-crop endpoint
        const response = await axios.post(
          `${BASE_URL}/business-gifts/${businessCode}/update-crop`,
          {
            imageUrl: imageData.imageUrl,
            cropData: serverCropData,
          }
        );

        if (response.data.success) {
          // Update the image in businessGiftImagesState
          setImages((prev) =>
            prev.map((img) => {
              if (img.imageId === imageData.imageId) {
                return {
                  ...img,
                  cropImageUrl: response.data.cropImageUrl,
                  viewImageUrl: response.data.viewImageUrl,
                  cropData: {
                    ...serverCropData,
                    naturalWidth: image_data.naturalWidth,
                    naturalHeight: image_data.naturalHeight,
                    ...cropbox_data,
                    zoomRatio: zoomvalue, // Save current zoom level
                    zoomFactor: imageData.cropData?.zoomFactor || 1.0, // Preserve border detection zoom
                  },
                  bwFilter: isBwFilter,
                  bluewhiteFilter: isBluewhiteFilter,
                };
              }
              return img;
            })
          );
        }

        handleCloseModal("editImageBusinessGifts");
      } catch (error) {
        console.error("[BusinessGifts] Error updating crop:", error);
        // Still update filters and crop data locally even if API call fails
        setImages((prev) =>
          prev.map((img) => {
            if (img.imageId === imageData.imageId) {
              return {
                ...img,
                cropData: {
                  ...serverCropData,
                  naturalWidth: image_data.naturalWidth,
                  naturalHeight: image_data.naturalHeight,
                  ...cropbox_data,
                  zoomRatio: zoomvalue,
                  zoomFactor: imageData.cropData?.zoomFactor || 1.0, // Preserve border detection zoom
                },
                bwFilter: isBwFilter,
                bluewhiteFilter: isBluewhiteFilter,
              };
            }
            return img;
          })
        );
        handleCloseModal("editImageBusinessGifts");
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    }
  };

  const isLowImgSize = +imageData.imageHeight >= 800 && +imageData.imageWidth >= 800;

  // Apply frame style to cropper container
  const el = document.querySelector(".cropper-container");
  if (el) {
    switch (selectedFrame) {
      case "bold":
      case "nordic":
        el.style.cssText += "transform: scale(1.35) translateY(33px);";
        break;
      case "ever":
        el.style.cssText += "transform: scale(1.37) translateY(33px);";
        break;
      case "clean":
        el.style.cssText += "transform: scale(1.07) translateY(7px);";
        break;
    }
  }

  return (
    <div id="edit-modal">
      {width > 767 ? (
        <div className="edit-modal__header">
          <button
            disabled={!isInitialLoaded && isLoading}
            onClick={() => getCropData()}
            className="edit"
          >
            סיום
          </button>
          <button
            onClick={() => handleCloseModal("editImageBusinessGifts")}
            className="close"
          >
            <img src="/assets/file/images/cross.svg" alt="close" />
          </button>
        </div>
      ) : (
        <button
          className="mobile-close-btn"
          onClick={() => handleCloseModal("editImageBusinessGifts")}
        >
          <img src="/assets/file/images/cross.svg" alt="close" />
        </button>
      )}

      <div className="edit-modal__content">
        {width > 767 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              position: "absolute",
              marginLeft: "75px",
              marginTop: "15px",
            }}
          >
            <div
              className="black-white-filter"
              onClick={handleFilterClick}
              style={{ position: "relative", margin: "0" }}
            >
              <img
                src={`/assets/images/bw-filter-button${isBwFilter ? "-clicked" : ""}.svg`}
                alt="black and white filter"
              />
            </div>
          </div>
        )}

        <div className="modal-title-mob">
          {isLowImgSize ? (
            <p style={{ fontWeight: "400", fontSize: "14px" }}>
              <span style={{ fontSize: "18px", fontWeight: "500" }}>הזזה והגדלה</span>
              <br />
              צבטו כדי להגדיל ולהזיז
            </p>
          ) : (
            <p>
              <img src="/assets/images/editmove.svg" />
              ניתן להזיז את התמונה בתוך המסגרת
            </p>
          )}
        </div>

        <div
          className={`${selectedFrame} cropper-frame`}
          style={{ marginTop: "24px", boxShadow: "0 3px 7px rgb(0 0 0 / 5%)" }}
        >
          {imageData.imageUrl ? (
            <>
              {isInitialLoaded && (
                <div
                  id={`${width > 767 ? "deskedit-in-frame-loader" : "mobiledit-in-frame-loader"}`}
                />
              )}
              {/* filter is applied to .cropper-container via these classes (EditImage/style.css), not to this div:
                  .cropper-container is transform-enlarged past this div's box and iOS left the overflow in colour */}
              <div className={`${isBwFilter ? "edit-cropper--bw" : ""} ${isBluewhiteFilter ? "edit-cropper--bluewhite" : ""}`}>
                <Cropper
                  ref={cropperRef}
                  style={{
                    height: getCropperSize(),
                    width: getCropperSize(),
                    transform: "rotationimage",
                    margin: "0 auto",
                  }}
                  zoomable
                  initialAspectRatio={1}
                  preview=".img-preview"
                  checkCrossOrigin={true}
                  src={imageData.imageUrl}
                  viewMode={3}
                  autoCropArea={1}
                  guides={false}
                  toggleDragModeOnDblclick
                  ready={(e) => {
                    InitialSets(e.target.cropper);
                  }}
                  dragMode="move"
                  background
                  responsive
                  checkOrientation={false}
                  zoom={handleOnZoom}
                  center={false}
                />
              </div>
            </>
          ) : (
            <div>No image selected</div>
          )}
        </div>

        {isLowImgSize && (
          <div className="rang_container">
            <div onClick={() => zoomvalueminus()} />
            <input
              type="range"
              min={`${zoomMinValue}`}
              max={`${getZoomLevel(+imageData.imageHeight, +imageData.imageWidth)}`}
              value={zoomvalue}
              onChange={handleZoomTo}
              step={`${ZOOM_STEP}`}
              id="zoomer"
              className="slider"
            />
            <img
              src="/assets/file/images/plus_icon.svg"
              onClick={() => zoomvalueplus()}
              alt="plus-icon"
            />
          </div>
        )}

        <div className="modal-title-desk">
          {isLowImgSize ? (
            <p style={{ fontWeight: "500", fontSize: "14px" }}>
              הזיזו והגדילו בעזרת העכבר
            </p>
          ) : (
            <p>ניתן להזיז את התמונה בתוך המסגרת בעזרת העכבר</p>
          )}
        </div>

        {width < 767 && (
          <div
            style={{
              position: "absolute",
              left: "6%",
              bottom: "72px",
              display: "flex",
              flexDirection: "row",
              gap: 6,
              zIndex: 10,
            }}
          >
            <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-end", gap: "6px" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ height: "28px", display: "flex", alignItems: "flex-end" }}>
                  <span className="filter-tag-greyscale-mobile">פופולארי</span>
                </div>
                <img
                  src={`/assets/images/bw-filter-button${isBwFilter ? "-clicked" : ""}.svg`}
                  alt="תמונות בשחור לבן"
                  onClick={handleFilterClick}
                  style={{ cursor: "pointer", width: 40, height: 40 }}
                />
              </div>
            </div>
          </div>
        )}

        {width <= 767 && (
          <button className="mobile-save-button" onClick={() => getCropData()}>
            {isLoading && !isInitialLoaded ? (
              <div className="ldio-kv0ui0pfesk-edit" />
            ) : (
              <>אישור</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default EditImageBusinessGifts;
